// Puente nativo (Capacitor) para la app de encargados.
// Todo aquí es no-op cuando no se ejecuta en un dispositivo nativo (web/PWA),
// de modo que el mismo código sirve para el build normal y para el de encargados.
import { Capacitor, registerPlugin } from '@capacitor/core'
import { api } from './api'

export const IS_ENCARGADO_APP = import.meta.env.VITE_ROLE === 'encargado'

function isNative(): boolean {
  try {
    return Capacitor.isNativePlatform()
  } catch {
    return false
  }
}

function platform(): 'android' | 'ios' | 'web' {
  try {
    return Capacitor.getPlatform() as 'android' | 'ios' | 'web'
  } catch {
    return 'web'
  }
}

// Navegación diferida (se la inyecta App.vue para abrir /pedidos al tocar el push).
let navigate: ((path: string) => void) | null = null
export function setNativeNavigate(fn: (path: string) => void) {
  navigate = fn
}

// ---- Push notifications ----------------------------------------------------

let pushInited = false

export async function initPush(): Promise<void> {
  if (!isNative() || pushInited) return
  pushInited = true
  const { PushNotifications } = await import('@capacitor/push-notifications')

  PushNotifications.addListener('registration', async (token) => {
    try {
      await api.post('/api/push/register', { push_token: token.value, platform: platform() })
    } catch {
      pushInited = false // permite reintentar en el próximo arranque
    }
  })

  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const data = action.notification?.data || {}
    if (data.type === 'purchase_request') {
      navigate?.('/pedidos')
    } else if (data.type === 'dispatch') {
      // Reparto de personal: abrir la pantalla de obras/tareas asignadas.
      navigate?.('/')
    }
  })

  const perm = await PushNotifications.requestPermissions()
  if (perm.receive === 'granted') {
    await PushNotifications.register()
  }
}

// ---- Ubicación en segundo plano -------------------------------------------

interface BgLocation {
  latitude: number
  longitude: number
  accuracy: number
}
interface BackgroundGeolocationPlugin {
  addWatcher(
    options: {
      backgroundMessage?: string
      backgroundTitle?: string
      requestPermissions?: boolean
      stale?: boolean
      distanceFilter?: number
    },
    callback: (location?: BgLocation, error?: { code: string }) => void,
  ): Promise<string>
  removeWatcher(options: { id: string }): Promise<void>
}

const BackgroundGeolocation = (() => {
  try {
    return registerPlugin<BackgroundGeolocationPlugin>('BackgroundGeolocation')
  } catch {
    return null
  }
})()

let watcherId: string | null = null
let lastSentAt = 0
const MIN_PING_INTERVAL_MS = 90_000 // antifloodo cliente; el servidor también filtra
const DISTANCE_FILTER_M = 75

export async function startLocationTracking(): Promise<void> {
  if (!isNative() || !IS_ENCARGADO_APP || !BackgroundGeolocation || watcherId) return
  watcherId = await BackgroundGeolocation.addWatcher(
    {
      backgroundTitle: 'Fichaje activo',
      backgroundMessage: 'Registrando ubicación durante la jornada.',
      requestPermissions: true,
      stale: false,
      distanceFilter: DISTANCE_FILTER_M,
    },
    (location, error) => {
      if (error || !location) return
      const now = Date.now()
      if (now - lastSentAt < MIN_PING_INTERVAL_MS) return
      lastSentAt = now
      void api
        .post('/api/location/ping', {
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy,
        })
        .catch(() => {})
    },
  )
}

export async function stopLocationTracking(): Promise<void> {
  if (!BackgroundGeolocation || !watcherId) return
  try {
    await BackgroundGeolocation.removeWatcher({ id: watcherId })
  } catch {
    /* noop */
  }
  watcherId = null
}
